import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Building2, Network, Users, TrendingUp, FileText } from 'lucide-react';

export const HospitalityResortsAdvisory = () => {
  return (
    <div className="service-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb-container">
        <Link to="/" className="breadcrumb-link">
          <ArrowLeft size={18} />
          Back to Home
        </Link>
        <span className="breadcrumb-separator">/</span>
        <Link to="/services/real-estate" className="breadcrumb-link">Real Estate Advisory</Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Hospitality & Resorts</span>
      </div>

      {/* Hero Section */}
      <section className="service-detail-hero">
        <div className="service-detail-icon">
          <Building2 size={48} />
        </div>
        <h1 className="service-detail-title">Hospitality & Resorts Advisory</h1>
        <p className="service-detail-subtitle">
          Strategic hospitality real estate transactions and hotel brand/operator onboarding solutions for property owners, developers and investors.
        </p>
      </section>

      {/* What We Do */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <h2 className="service-section-title">What We Do</h2>
          <p className="service-section-text" style={{maxWidth: '900px', margin: '0 auto 3rem', textAlign: 'center'}}>
            HSNL Advisory provides advisory and transaction facilitation services across the hospitality and resort sector, supporting hotel owners, developers, investors and hospitality operators in identifying and executing strategic opportunities.
          </p>

          {/* Two Main Service Cards */}
          <div className="hospitality-main-services">
            {/* Hotel & Resort Transactions */}
            <div className="hospitality-service-block">
              <div className="service-block-icon">
                <Building2 size={36} />
              </div>
              <h3 className="service-block-title">Hotel & Resort Transactions</h3>
              <p className="service-block-intro">
                We facilitate the buying and selling of hospitality assets and opportunities across different stages of development and operation.
              </p>
              <ul className="service-block-list">
                <li>Hotel & resort land parcels</li>
                <li>Operational hotels and resorts</li>
                <li>Non-operational or closed hospitality properties</li>
                <li>Existing hotel and resort buildings</li>
                <li>Hospitality properties available for acquisition</li>
                <li>Assets suitable for redevelopment or conversion into hotels and resorts</li>
              </ul>
              <p className="service-block-closing">
                We work with property owners, developers, investors and prospective buyers to facilitate introductions, commercial discussions and transaction opportunities.
              </p>
            </div>

            {/* Hotel Brand & Operator Onboarding */}
            <div className="hospitality-service-block">
              <div className="service-block-icon">
                <Network size={36} />
              </div>
              <h3 className="service-block-title">Hotel Brand & Operator Onboarding</h3>
              <p className="service-block-intro">
                We assist hotel and resort owners in identifying and engaging suitable hospitality brands and operators for their properties.
              </p>
              <p className="service-block-subtext">
                Highlight the commercial structures we work across:
              </p>
            </div>
          </div>

          {/* Three Commercial Structure Cards */}
          <div className="commercial-structures-grid">
            <div className="commercial-structure-card">
              <div className="structure-icon">
                <Users size={28} />
              </div>
              <h4 className="structure-title">Management Contract</h4>
              <p className="structure-description">
                Connecting hotel owners with professional hospitality operators to manage and operate their properties.
              </p>
            </div>

            <div className="commercial-structure-card">
              <div className="structure-icon">
                <TrendingUp size={28} />
              </div>
              <h4 className="structure-title">Revenue Sharing</h4>
              <p className="structure-description">
                Facilitating operator structures where commercial returns are linked to the property's operating revenues.
              </p>
            </div>

            <div className="commercial-structure-card">
              <div className="structure-icon">
                <FileText size={28} />
              </div>
              <h4 className="structure-title">Leasing</h4>
              <p className="structure-description">
                Connecting property owners with hospitality operators interested in operating assets under long-term lease structures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Brands We Currently Deal With */}
      <section className="service-detail-section alt-bg">
        <div className="service-detail-content">
          <h2 className="service-section-title">Brands We Currently Deal With</h2>
          <p className="service-section-text" style={{textAlign: 'center', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem'}}>
            We engage with hospitality brands and operators across different market segments to evaluate suitable operating and commercial structures for hotel and resort owners.
          </p>

          <div className="hospitality-brands-premium-grid">
            <div className="brand-logo-premium-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/ynw4td9f_Sunday%20Hotel%20Logo.jpg" 
                alt="Sunday Hotels" 
                className="brand-logo-premium"
              />
            </div>
            <div className="brand-logo-premium-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/hr1pc1h6_Sterling%20Hotel%20logo.png" 
                alt="Sterling Resorts" 
                className="brand-logo-premium"
              />
            </div>
            <div className="brand-logo-premium-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/m4rh58fo_saltstayz-logo.png" 
                alt="SALT Stayz" 
                className="brand-logo-premium"
              />
            </div>
            <div className="brand-logo-premium-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/1uuakn8n_Pride%20Hotel%20Logo.jpg" 
                alt="Pride Hotels & Resorts" 
                className="brand-logo-premium"
              />
            </div>
            <div className="brand-logo-premium-card">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_exim-solutions-2/artifacts/9sx79c57_Alturist.png" 
                alt="The Altruist Hotels" 
                className="brand-logo-premium"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-detail-section">
        <div className="service-detail-content">
          <div className="service-cta-box">
            <h2 className="cta-title">Discuss a Hospitality Opportunity</h2>
            <p className="cta-text">
              Whether you are looking to acquire or divest a hospitality asset, or explore a suitable hotel brand/operator for your property, connect with HSNL Advisory to discuss the opportunity.
            </p>
            <Link to="/#contact" className="btn-primary">
              Discuss an Opportunity
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
